// Eskom PARCC — self-service account settings.
//
// A controlled modal for the signed-in user to manage their own profile: display
// name, avatar (photo URL), job title, phone and department. Edits go through the
// server account endpoint (adminApi.getProfile / updateProfile), NOT client-direct
// Firebase: the server verifies identity from the token, writes both Firebase Auth
// and the DB `User` row, and records an audited `update_profile` entry — so the
// change is integritous and "by whom" is captured. Email is the sign-in identity
// and is not editable here. Password self-service lives in the separate
// ChangePassword modal.
//
// Rendered through a portal to <body>: the modal must NOT live inside <header>
// (its real DOM parent), whose `backdrop-filter` makes it the containing block for
// `position: fixed` descendants — which would size this fixed overlay to the
// header and ride it up into the nav. (Same fix as ChangePassword.)
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auth } from '../../lib/firebase';
import { adminApi, type AccountProfileInput } from '../../lib/adminApi';
import { uploadAvatar, fetchStorageObjectUrl } from '../../lib/storage';
import { isStoragePath } from '../../lib/storagePaths';

type Form = Required<{ [K in keyof AccountProfileInput]: string }>;
const EMPTY_FORM: Form = { displayName: '', photoUrl: '', jobTitle: '', phone: '', department: '' };

/** Collapse a trimmed field to `null` (the unambiguous "unset") for the server. */
function orNull(value: string): string | null {
  const t = value.trim();
  return t === '' ? null : t;
}

export function AccountModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [status, setStatus] = useState<{ kind: 'ok' | 'bad'; message: string } | null>(null);
  // Resolved, renderable avatar URL (a `blob:` object URL for stored objects, or a
  // legacy absolute URL as-is). `uploading` gates the picker while a file is in flight.
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // The current in-memory object URL, so we can revoke it when replaced/unmounted.
  const objectUrlRef = useRef<string | null>(null);

  /** Swap the preview, revoking any prior `blob:` object URL we created. */
  const showPreview = useCallback((url: string | null, isObjectUrl: boolean): void => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = isObjectUrl ? url : null;
    setPreview(url);
  }, []);

  // Load the caller's own profile when the modal opens.
  const profileQuery = useQuery({
    queryKey: ['accountProfile'],
    queryFn: () => adminApi.getProfile(),
    enabled: open,
    staleTime: 0,
  });

  // Populate the form from the fetched profile (and re-sync if it refetches), and
  // resolve the saved avatar for display: a stored object path is fetched via the
  // Storage SDK (authenticated) into a blob: URL; a legacy absolute URL is used
  // as-is. A failed resolve leaves no preview (the initials fallback shows).
  useEffect(() => {
    if (!(open && profileQuery.data)) return;
    const d = profileQuery.data;
    setForm({
      displayName: d.displayName ?? '',
      photoUrl: d.photoUrl ?? '',
      jobTitle: d.jobTitle ?? '',
      phone: d.phone ?? '',
      department: d.department ?? '',
    });
    const value = (d.photoUrl ?? '').trim();
    if (!value) {
      showPreview(null, false);
      return;
    }
    if (!isStoragePath(value)) {
      showPreview(value, false);
      return;
    }
    let cancelled = false;
    void fetchStorageObjectUrl(value)
      .then((url) => {
        if (cancelled) URL.revokeObjectURL(url);
        else showPreview(url, true);
      })
      .catch(() => {
        if (!cancelled) showPreview(null, false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, profileQuery.data, showPreview]);

  // Revoke any lingering object URL when the modal unmounts.
  useEffect(
    () => () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    [],
  );

  const saveMutation = useMutation({
    mutationFn: (input: AccountProfileInput) => adminApi.updateProfile(input),
    onSuccess: async () => {
      // Refresh the Firebase user so the header reflects the new name/photo, and
      // re-read the saved profile.
      await auth.currentUser?.reload();
      await queryClient.invalidateQueries({ queryKey: ['accountProfile'] });
      setStatus({ kind: 'ok', message: 'Profile updated.' });
    },
    onError: (err: unknown) => {
      setStatus({
        kind: 'bad',
        message: err instanceof Error ? err.message : 'Unable to update your profile.',
      });
    },
  });

  if (!open) return null;

  function set<K extends keyof Form>(key: K, value: string): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleClose(): void {
    setStatus(null);
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setStatus(null);
    saveMutation.mutate({
      displayName: orNull(form.displayName),
      photoUrl: orNull(form.photoUrl),
      jobTitle: orNull(form.jobTitle),
      phone: orNull(form.phone),
      department: orNull(form.department),
    });
  }

  /** Upload the chosen image, show it immediately, and stash the object path. */
  async function handleFile(file: File): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setStatus({ kind: 'bad', message: 'You must be signed in to change your photo.' });
      return;
    }
    setStatus(null);
    setUploading(true);
    // Instant local preview while the upload completes (kept as the preview after,
    // so we avoid an immediate round-trip back through Storage).
    showPreview(URL.createObjectURL(file), true);
    try {
      const path = await uploadAvatar(uid, file);
      set('photoUrl', path);
    } catch (err) {
      showPreview(null, false);
      setStatus({
        kind: 'bad',
        message: err instanceof Error ? err.message : 'Could not upload that image.',
      });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  /** Clear the avatar (saved on the next Save). */
  function removePhoto(): void {
    set('photoUrl', '');
    showPreview(null, false);
  }

  const submitting = saveMutation.isPending;
  const busy = submitting || uploading;

  return createPortal(
    <div
      className="password-modal-backdrop open"
      role="dialog"
      aria-modal="true"
      aria-labelledby="accountHeading"
    >
      <div className="password-modal-card">
        <h3 id="accountHeading">My account</h3>
        <p>
          Update the details shown across the workspace. Your email is your sign-in identity and
          cannot be changed here.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="account-identity">
            {preview ? (
              <img
                className="account-avatar"
                src={preview}
                alt=""
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                }}
              />
            ) : (
              <div className="account-avatar account-avatar-empty" aria-hidden="true">
                {(form.displayName.trim()[0] ?? '?').toUpperCase()}
              </div>
            )}
            <div className="account-avatar-controls">
              <input
                ref={fileRef}
                id="accountPhotoFile"
                className="account-avatar-file"
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                aria-label="Upload a profile photo"
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFile(f);
                }}
              />
              <div className="small">
                {uploading
                  ? 'Uploading…'
                  : profileQuery.isLoading
                    ? 'Loading your profile…'
                    : 'PNG, JPEG, GIF, WebP or SVG · up to 5 MB.'}
              </div>
              {form.photoUrl.trim() !== '' && !uploading && (
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={removePhoto}
                  disabled={busy}
                >
                  Remove photo
                </button>
              )}
            </div>
          </div>

          <label htmlFor="accountDisplayName">
            Display name
            <input
              id="accountDisplayName"
              type="text"
              autoComplete="name"
              value={form.displayName}
              onChange={(e) => set('displayName', e.target.value)}
              disabled={busy}
            />
          </label>

          <label htmlFor="accountJobTitle">
            Job title
            <input
              id="accountJobTitle"
              type="text"
              autoComplete="organization-title"
              value={form.jobTitle}
              onChange={(e) => set('jobTitle', e.target.value)}
              disabled={busy}
            />
          </label>

          <label htmlFor="accountPhone">
            Phone
            <input
              id="accountPhone"
              type="tel"
              autoComplete="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              disabled={busy}
            />
          </label>

          <label htmlFor="accountDepartment">
            Department
            <input
              id="accountDepartment"
              type="text"
              autoComplete="organization"
              value={form.department}
              onChange={(e) => set('department', e.target.value)}
              disabled={busy}
            />
          </label>

          {status && (
            <div
              id="accountStatus"
              className={status.kind === 'bad' ? 'bad' : 'ok'}
              role={status.kind === 'bad' ? 'alert' : 'status'}
            >
              {status.message}
            </div>
          )}

          <div className="password-modal-actions">
            <button type="button" className="btn ghost" onClick={handleClose} disabled={busy}>
              Close
            </button>
            <button type="submit" className="btn primary" disabled={busy}>
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
